import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import { Download, Copy } from "lucide-react";
import { toast } from "sonner";
import { slugifyCampaign } from "@shared/qrCodes";

/**
 * Black on white, error correction M, a 4-module quiet zone. Plain is what
 * every phone camera and print shop handles reliably; a coloured or logo'd
 * code is a print risk for no tracking gain.
 */
const OPTS = { errorCorrectionLevel: "M" as const, margin: 4, color: { dark: "#000000", light: "#ffffff" } };

function save(href: string, filename: string) {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function downloadQrSvg(url: string, filename: string) {
  const svg = await QRCode.toString(url, { ...OPTS, type: "svg" });
  const href = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  save(href, filename);
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

export async function downloadQrPng(url: string, filename: string) {
  save(await QRCode.toDataURL(url, { ...OPTS, width: 1024 }), filename);
}

export function QrImage({ url, size = 192 }: { url: string; size?: number }) {
  const [svg, setSvg] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toString(url, { ...OPTS, type: "svg" }).then((s) => live && setSvg(s));
    return () => { live = false; };
  }, [url]);
  // Always on white, even in dark mode: a scanner needs the contrast.
  return (
    <div className="rounded-md bg-white p-1" style={{ width: size, height: size }}>
      {svg && <img alt={`QR code for ${url}`} width={size - 8} height={size - 8} src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`} />}
    </div>
  );
}

export function QrDownloads({ url, name }: { url: string; name: string }) {
  const base = `qr-${slugifyCampaign(name)}`;
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" onClick={() => downloadQrSvg(url, `${base}.svg`)}>
        <Download className="mr-1 h-3.5 w-3.5" /> SVG (print)
      </Button>
      <Button size="sm" variant="outline" onClick={() => downloadQrPng(url, `${base}.png`)}>
        <Download className="mr-1 h-3.5 w-3.5" /> PNG
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() =>
          navigator.clipboard.writeText(url).then(
            () => toast.success("Link copied"),
            () => toast.error("Could not copy the link"),
          )
        }
      >
        <Copy className="mr-1 h-3.5 w-3.5" /> Copy link
      </Button>
    </div>
  );
}
